import { UpdateTask } from '@griply/types';

import { BatchDocuments, WriteBatchFn } from '../batch-documents';
import { dateToFirestoreTimestamp } from '../converters/date-to-firestore-timestamp';
import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type UpdateTasksOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  tasks: UpdateTask[];
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (collectionRef: NoInfer<TCollectionRef>, id: string) => TDocRef;
  updateDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  writeBatch?: WriteBatchFn<NoInfer<TDocRef>, NoInfer<TDb>>;
  onWriteError: (error: unknown) => void;
};

export const updateTasks = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: UpdateTasksOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<void> => {
  const {
    tasks,
    collection,
    doc,
    updateDoc,
    db,
    serverTimestamp,
    timestampFromDate,
    writeBatch,
    onWriteError,
  } = options;

  if (!tasks.length) {
    return;
  }

  const taskCollection = collection(db, FirestoreCollections.Tasks);

  const buildPayload = (task: UpdateTask) => ({
    ...(dateToFirestoreTimestamp(task, timestampFromDate) as Record<
      string,
      unknown
    >),
    updatedAt: serverTimestamp(),
  });

  if (tasks.length === 1 || !writeBatch) {
    tasks.forEach((task) => {
      detachWrite(
        updateDoc(doc(taskCollection, task.id), buildPayload(task)),
        onWriteError,
      );
    });
    return;
  }

  const batchDocuments = new BatchDocuments(db, writeBatch);
  tasks.forEach((task) => {
    batchDocuments.updateDocument({
      ref: doc(taskCollection, task.id),
      data: buildPayload(task),
    });
  });
  detachWrite(batchDocuments.commit(), onWriteError);
};
